import { useState, useEffect, useCallback, useMemo } from 'react';
import { seminareAPI } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import {
  GraduationCap, Users, UserPlus, Download, RefreshCw, Search, ExternalLink,
  XCircle, RotateCcw, Loader2, Link2Off, CheckCircle2,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';

const QUELLE_LABEL = { 'seminar-landingpage': 'Anmeldeseite', empfang: 'Empfang' };

function fmtZeit(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('de-DE', {
      day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
}

export default function SeminarePage() {
  const { userPermissions, isAdminPermissions } = useAuth();
  const darfVerwalten = isAdminPermissions || !!userPermissions?.['seminare.verwalten'];

  const [daten, setDaten] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('');
  const [suche, setSuche] = useState('');
  const [busyId, setBusyId] = useState(null);

  // Anmeldung am Empfang
  const [dialogSeminar, setDialogSeminar] = useState(null);
  const [neuName, setNeuName] = useState('');
  const [neuMitglied, setNeuMitglied] = useState('');
  const [speichert, setSpeichert] = useState(false);

  const laden = useCallback(async (stillNachladen = false) => {
    if (!stillNachladen) setLoading(true);
    try {
      const res = await seminareAPI.uebersicht();
      setDaten(res.data);
      setTab((t) => t || res.data.seminare?.[0]?.id || '');
    } catch {
      toast.error('Anmeldungen konnten nicht geladen werden.');
    }
    setLoading(false);
  }, []);
  useEffect(() => { laden(); }, [laden]);

  const seminare = daten?.seminare || [];
  const aktuell = useMemo(() => seminare.find((s) => s.id === tab) || null, [seminare, tab]);

  const gefiltert = useMemo(() => {
    const q = suche.trim().toLowerCase();
    const liste = aktuell?.anmeldungen || [];
    if (!q) return liste;
    return liste.filter((a) =>
      (a.name || '').toLowerCase().includes(q) ||
      (a.mitglied || '').toLowerCase().includes(q) ||
      (a.member_name || '').toLowerCase().includes(q));
  }, [aktuell, suche]);

  const absagen = async (a) => {
    if (!window.confirm(`Anmeldung von ${a.name} für "${aktuell.titel}" wirklich absagen? Der Platz wird sofort wieder frei.`)) return;
    setBusyId(a.id);
    try {
      await seminareAPI.absagen(a.id);
      toast.success('Abgesagt — Platz ist wieder frei.');
      await laden(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Absage fehlgeschlagen.');
    }
    setBusyId(null);
  };

  const wiederAnmelden = async (a) => {
    setBusyId(a.id);
    try {
      await seminareAPI.wiederAnmelden(a.id);
      toast.success('Wieder angemeldet.');
      await laden(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Wieder-Anmelden fehlgeschlagen.');
    }
    setBusyId(null);
  };

  const anmelden = async () => {
    if (neuName.trim().length < 2 || neuMitglied.trim().length < 2) {
      toast.error('Bitte Name und Mitgliedsnummer/E-Mail angeben.');
      return;
    }
    setSpeichert(true);
    try {
      await seminareAPI.anmelden({
        seminar_id: dialogSeminar.id,
        name: neuName.trim(),
        mitglied: neuMitglied.trim(),
      });
      toast.success(`${neuName.trim()} ist für "${dialogSeminar.titel}" angemeldet.`);
      setDialogSeminar(null);
      setNeuName(''); setNeuMitglied('');
      await laden(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Anmeldung fehlgeschlagen.');
    }
    setSpeichert(false);
  };

  const exportieren = async () => {
    try {
      const res = await seminareAPI.exportCSV();
      const url = URL.createObjectURL(new Blob([res.data], { type: 'text/csv;charset=utf-8' }));
      const a = document.createElement('a');
      a.href = url; a.download = 'seminaranmeldungen.csv'; a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Export fehlgeschlagen.');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24 text-muted-foreground">
        <Loader2 className="h-5 w-5 animate-spin mr-2" /> Lädt …
      </div>
    );
  }

  const gesamtFrei = (daten?.kapazitaet_gesamt || 0) - (daten?.angemeldet_gesamt || 0);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <GraduationCap className="h-6 w-6 text-marke" /> Seminare
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Anmeldungen der Seminarwoche — von der Anmeldeseite und vom Empfang.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={() => laden(true)}>
            <RefreshCw className="h-4 w-4 mr-1.5" /> Aktualisieren
          </Button>
          <Button variant="outline" size="sm" onClick={exportieren}>
            <Download className="h-4 w-4 mr-1.5" /> CSV
          </Button>
          <Button variant="outline" size="sm" asChild>
            <a href="/seminar" target="_blank" rel="noreferrer">
              <ExternalLink className="h-4 w-4 mr-1.5" /> Anmeldeseite
            </a>
          </Button>
        </div>
      </div>

      {/* Überblick */}
      <div className="grid gap-4 sm:grid-cols-3">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Angemeldet</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{daten?.angemeldet_gesamt ?? 0}</div>
            <p className="text-xs text-muted-foreground mt-1">von {daten?.kapazitaet_gesamt ?? 0} Plätzen</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Noch frei</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{gesamtFrei}</div>
            <p className="text-xs text-muted-foreground mt-1">über alle fünf Seminare</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Ohne Zuordnung</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{daten?.ohne_zuordnung ?? 0}</div>
            <p className="text-xs text-muted-foreground mt-1">
              {daten?.ohne_zuordnung ? 'Keine Mitgliedsakte gefunden — bitte prüfen' : 'Alle einem Mitglied zugeordnet'}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Seminare */}
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="flex flex-wrap h-auto">
          {seminare.map((s) => (
            <TabsTrigger key={s.id} value={s.id} className="flex-col items-start py-2 px-3">
              <span className="font-medium">{s.tag}</span>
              <span className="text-xs text-muted-foreground">
                {s.titel} · {s.angemeldet}/{s.kapazitaet}
              </span>
            </TabsTrigger>
          ))}
        </TabsList>

        {seminare.map((s) => (
          <TabsContent key={s.id} value={s.id} className="mt-4">
            <Card>
              <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-3 space-y-0">
                <div>
                  <CardTitle className="text-lg">{s.titel}</CardTitle>
                  <p className="text-sm text-muted-foreground mt-0.5">
                    {s.tag} · 18–20 Uhr · mit {s.coach}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  {s.frei === 0
                    ? <Badge variant="destructive">Ausgebucht</Badge>
                    : <Badge variant="secondary">{s.frei} frei</Badge>}
                  {s.abgesagt > 0 && (
                    <Badge variant="outline">{s.abgesagt} abgesagt</Badge>
                  )}
                  {darfVerwalten && (
                    <Button size="sm" disabled={s.frei === 0} onClick={() => setDialogSeminar(s)}>
                      <UserPlus className="h-4 w-4 mr-1.5" /> Anmelden
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {s.anmeldungen.length > 3 && (
                  <div className="relative mb-3">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      className="pl-9"
                      placeholder="Nach Name oder Mitglied suchen …"
                      value={suche}
                      onChange={(e) => setSuche(e.target.value)}
                    />
                  </div>
                )}

                {gefiltert.length === 0 ? (
                  <div className="text-center py-10 text-muted-foreground">
                    <Users className="h-8 w-8 mx-auto mb-2 opacity-40" />
                    {s.anmeldungen.length === 0 ? 'Noch keine Anmeldungen.' : 'Kein Treffer.'}
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b text-left text-muted-foreground">
                          <th className="py-2 pr-3 font-medium">Name</th>
                          <th className="py-2 pr-3 font-medium">Angegeben</th>
                          <th className="py-2 pr-3 font-medium">Im CRM</th>
                          <th className="py-2 pr-3 font-medium">Über</th>
                          <th className="py-2 pr-3 font-medium">Angemeldet</th>
                          <th className="py-2 font-medium text-right">Aktion</th>
                        </tr>
                      </thead>
                      <tbody>
                        {gefiltert.map((a) => {
                          const abgesagt = a.status === 'abgesagt';
                          return (
                            <tr key={a.id || a.mitglied_key} className={`border-b last:border-0 ${abgesagt ? 'opacity-55' : ''}`}>
                              <td className="py-2.5 pr-3 font-medium">
                                {a.name}
                                {abgesagt && <Badge variant="outline" className="ml-2 text-xs">abgesagt</Badge>}
                              </td>
                              <td className="py-2.5 pr-3 text-muted-foreground">{a.mitglied}</td>
                              <td className="py-2.5 pr-3">
                                {a.member_id ? (
                                  <a href={`/leads/${a.member_id}`} className="text-marke hover:underline inline-flex items-center gap-1">
                                    <CheckCircle2 className="h-3.5 w-3.5" /> {a.member_name}
                                  </a>
                                ) : (
                                  <span className="text-amber-600 inline-flex items-center gap-1" title="Kein Mitglied im CRM gefunden">
                                    <Link2Off className="h-3.5 w-3.5" /> nicht zugeordnet
                                  </span>
                                )}
                              </td>
                              <td className="py-2.5 pr-3 text-muted-foreground">
                                {QUELLE_LABEL[a.quelle] || a.quelle}
                                {a.erfasst_von_name ? ` (${a.erfasst_von_name})` : ''}
                              </td>
                              <td className="py-2.5 pr-3 text-muted-foreground whitespace-nowrap">{fmtZeit(a.erstellt_am)}</td>
                              <td className="py-2.5 text-right">
                                {darfVerwalten && (abgesagt ? (
                                  <Button variant="ghost" size="sm" disabled={busyId === a.id || s.frei === 0}
                                          onClick={() => wiederAnmelden(a)}>
                                    <RotateCcw className="h-4 w-4 mr-1.5" /> Wieder anmelden
                                  </Button>
                                ) : (
                                  <Button variant="ghost" size="sm" disabled={busyId === a.id}
                                          onClick={() => absagen(a)}
                                          className="text-muted-foreground hover:text-destructive">
                                    <XCircle className="h-4 w-4 mr-1.5" /> Absagen
                                  </Button>
                                ))}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        ))}
      </Tabs>

      {/* Anmeldung am Empfang */}
      <Dialog open={!!dialogSeminar} onOpenChange={(o) => !o && setDialogSeminar(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Anmelden — {dialogSeminar?.titel}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground -mt-2">
            {dialogSeminar?.tag} · 18–20 Uhr · mit {dialogSeminar?.coach} · {dialogSeminar?.frei} Plätze frei
          </p>
          <div className="space-y-3 mt-2">
            <div>
              <Label htmlFor="sem-name">Name</Label>
              <Input id="sem-name" value={neuName} onChange={(e) => setNeuName(e.target.value)}
                     placeholder="Vor- und Nachname" autoFocus />
            </div>
            <div>
              <Label htmlFor="sem-mitglied">Mitgliedsnummer oder E-Mail</Label>
              <Input id="sem-mitglied" value={neuMitglied} onChange={(e) => setNeuMitglied(e.target.value)}
                     placeholder="z. B. 1042 oder max@mail.de"
                     onKeyDown={(e) => e.key === 'Enter' && anmelden()} />
              <p className="text-xs text-muted-foreground mt-1">
                Wird automatisch mit der Mitgliedsakte verknüpft, wenn sie gefunden wird.
              </p>
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setDialogSeminar(null)}>Abbrechen</Button>
            <Button onClick={anmelden} disabled={speichert}>
              {speichert ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <UserPlus className="h-4 w-4 mr-1.5" />}
              Anmelden
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
